import { ActionIcon, Container, Flex, Grid, Stack, Switch, TextInput, Tooltip } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { IconCheck, IconEdit, IconPlus, IconTrash, IconX } from '@tabler/icons-react'
import Form from 'common/Form'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useMemo, useState } from 'react'
import { deliveryCompanySchema, shipmentTypeSchema } from 'types/schemas'
import { trimFormValues } from 'utils'
import z, { ZodError } from 'zod'
import EditCheckboxInput from './EditCheckboxInput'
import EditNumberInput from './EditNumberInput'

type DeliveryCompany = z.infer<typeof deliveryCompanySchema>
type ShipmentType = z.infer<typeof shipmentTypeSchema>

type Props = {
  onSubmit: (values: DeliveryCompany) => Promise<void>
  onClose: () => void
  initialValues?: DeliveryCompany
}

export default function DeliveryCompanyForm({ onSubmit, onClose, initialValues }: Props) {
  const form = useForm<DeliveryCompany>({
    validate: zodResolver(deliveryCompanySchema),
    initialValues: initialValues || {
      name: '',
      isActive: true,
      shipmentTypes: [],
    },
    transformValues: trimFormValues,
  })

  const [validationErrors, setValidationErrors] = useState<ZodError<ShipmentType> | null>(null)
  function removeFieldError(field: keyof ShipmentType) {
    setValidationErrors((prev) => {
      if (!prev) return null

      const filteredIssues = prev.issues.filter((issue) => issue.path[0] !== field)

      return filteredIssues.length > 0 ? new ZodError(filteredIssues) : null
    })
  }

  const columns = useMemo<Array<MRT_ColumnDef<ShipmentType>>>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nazwa',
        mantineEditTextInputProps: {
          error: validationErrors?.issues.find((issue) => issue.path[0] === 'name')?.message,
          onFocus: () => removeFieldError('name'),
        },
      },
      {
        accessorKey: 'isActive',
        header: 'Aktywny',
        Cell: ({ cell }) => {
          return cell.getValue() ? (
            <IconCheck stroke={2.5} color="green" size={22} />
          ) : (
            <IconX stroke={2.5} color="red" size={22} />
          )
        },
        Edit: (props) => (
          // @ts-ignore
          <EditCheckboxInput
            error={validationErrors?.issues.find((issue) => issue.path[0] === 'isActive')?.message}
            onFocus={() => removeFieldError('isActive')}
            {...props}
          />
        ),
      },
      {
        accessorKey: 'shippingCost',
        header: 'Koszt przesyłki',
        editVariant: 'text',
        Edit: (props) => (
          // @ts-ignore
          <EditNumberInput
            error={validationErrors?.issues.find((issue) => issue.path[0] === 'shippingCost')?.message}
            onFocus={() => removeFieldError('shippingCost')}
            {...props}
          />
        ),
      },
      {
        accessorKey: 'isParcelLocker',
        header: 'Paczkomat',
        Cell: ({ cell }) => {
          return cell.getValue() ? (
            <IconCheck stroke={2.5} color="green" size={22} />
          ) : (
            <IconX stroke={2.5} color="red" size={22} />
          )
        },
        Edit: (props) => (
          // @ts-ignore
          <EditCheckboxInput
            error={validationErrors?.issues.find((issue) => issue.path[0] === 'isParcelLocker')?.message}
            onFocus={() => removeFieldError('isParcelLocker')}
            {...props}
          />
        ),
      },
    ],
    [validationErrors]
  )

  const table = useMantineReactTable({
    columns,
    data: form.values.shipmentTypes,
    layoutMode: 'grid',
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    enableEditing: true,
    getRowId: (row) => row.id,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableTopToolbar: true,
    enableGlobalFilter: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableFilters: false,
    enableFullScreenToggle: false,
    enableBottomToolbar: form.values.shipmentTypes.length < 5 ? false : true,
    paginationDisplayMode: 'pages',
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    pageCount: 5,
    mantinePaginationProps: {
      rowsPerPageOptions: ['5', '10'],
    },
    initialState: {
      density: 'xs',
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 100,
        Cell: ({ row, table }) => {
          const editingRow = table.getState().editingRow
          const creatingRow = table.getState().creatingRow
          const isEditingRow = editingRow && editingRow.id === row.original.id
          const isCreatingRow = creatingRow && creatingRow.id === row.id
          if (isEditingRow || isCreatingRow) {
            return (
              <Flex gap="sm" justify="end">
                <Tooltip label="Zapisz">
                  <ActionIcon
                    radius="xl"
                    variant="light"
                    color="blue"
                    onClick={() => {
                      if (isEditingRow) {
                        const parsedValues = {
                          ...row._valuesCache,
                          id:
                            form.values.shipmentTypes.find(
                              (st) =>
                                st.name === row.original.name &&
                                st.shippingCost === row.original.shippingCost &&
                                st.isActive === row.original.isActive &&
                                st.isParcelLocker === row.original.isParcelLocker
                            )?.id || '',
                        }
                        const { error, success, data } = shipmentTypeSchema.safeParse(parsedValues)
                        setValidationErrors(error || null)
                        if (success) {
                          const shipmentTypes = [...form.values.shipmentTypes]
                          shipmentTypes[table.getState().editingRow!.index] = data as ShipmentType
                          form.setFieldValue('shipmentTypes', shipmentTypes)
                          table.setEditingRow(null)
                        }
                      } else {
                        const parsedValues = {
                          ...row._valuesCache,
                          id: form.values.shipmentTypes.length.toString(),
                        }
                        const { error, success, data } = shipmentTypeSchema.safeParse(parsedValues)
                        setValidationErrors(error || null)
                        if (success) {
                          const shipmentTypes = [...form.values.shipmentTypes, data as ShipmentType]
                          form.setFieldValue('shipmentTypes', shipmentTypes)
                          table.setCreatingRow(null)
                        }
                      }
                    }}
                  >
                    <IconCheck size={18} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Anuluj">
                  <ActionIcon
                    radius="xl"
                    variant="light"
                    color="red"
                    onClick={() => {
                      table.setEditingRow(null)
                      table.setCreatingRow(null)
                      setValidationErrors(null)
                    }}
                  >
                    <IconX size={18} />
                  </ActionIcon>
                </Tooltip>
              </Flex>
            )
          } else {
            return (
              <Flex gap="sm" justify="end">
                <Tooltip label="Edytuj">
                  <ActionIcon radius="xl" variant="light" color="gray" onClick={() => table.setEditingRow(row)}>
                    <IconEdit size={18} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Usuń">
                  <ActionIcon
                    radius="xl"
                    variant="light"
                    color="red"
                    onClick={() => {
                      const shipmentTypes = [...form.values.shipmentTypes]
                      shipmentTypes.splice(row.index, 1)
                      form.setFieldValue('shipmentTypes', shipmentTypes)
                    }}
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </Tooltip>
              </Flex>
            )
          }
        },
      },
    },
    renderTopToolbarCustomActions: () => (
      <Container mr={0} pr={0}>
        <Tooltip label="Dodaj metodę dostawy">
          <ActionIcon size="lg" variant="subtle" onClick={() => table.setCreatingRow(true)} ml="auto">
            <IconPlus />
          </ActionIcon>
        </Tooltip>
      </Container>
    ),
  })

  return (
    <Form
      onSubmit={onSubmit}
      form={form}
      onCancel={onClose}
      submitText={initialValues ? 'Zapisz zmiany' : 'Dodaj kuriera'}
      disabledReason={form.values.shipmentTypes.length === 0 ? 'Dodaj przynajmniej jedną metodę dostawy' : null}
    >
      <Stack gap="xs">
        <Grid>
          <Grid.Col span={6}>
            <TextInput label="Nazwa kuriera" placeholder="InPost" withAsterisk {...form.getInputProps('name')} />
          </Grid.Col>
          <Grid.Col span={6} display="flex" style={{ alignItems: 'end' }}>
            <Switch
              label="Aktywny"
              description="Nieaktywna nie będzie dostępna podczas składania zamówienia"
              {...form.getInputProps('isActive', { type: 'checkbox' })}
            />
          </Grid.Col>
        </Grid>

        <MantineReactTable table={table} />
      </Stack>
    </Form>
  )
}
